<script setup lang="ts">
import { QaEquipmentCatalogDto } from '@/api/generator';
import ConfirmedAction from '@/components/confirmationModals/ConfirmedAction.vue';
import { useDeviceStatusSignalR } from '@/components/deviceStatus/utils/useDeviceStatusSignalR';
import ZeroMeasurementPairRow from '@/components/measurement/ZeroMeasurementPairRow.vue';
import ZeroMeasurementStartButton from '@/components/measurement/ZeroMeasurementStartButton.vue';
import { useLatestZeroMeasurement } from '@/components/measurement/utils/useLatestZeroMeasurement';
import { isActiveMeasurementStatus } from '@/components/measurement/utils/useMeasurement';
import { useZeroMeasurement } from '@/components/measurement/utils/useZeroMeasurement';
import { useZeroMeasurementBlocker } from '@/components/measurement/utils/useZeroMeasurementBlocker';
import RTSystemTile from '@/components/RTSystemTile/RTSystemTile.vue';
import {
  isBeamAnalysisPair,
  orderSystemsForBeamAnalysis,
} from '@/views/tasks/components/QaSectionUtil';
import { useTranslation } from 'i18next-vue';
import { computed, ref, watch } from 'vue';
import BeamAnalysisDetectorConfirmation from './BeamAnalysisDetectorConfirmation.vue';
import { useDetectorConfirmation } from './useDetectorConfirmation';

/**
 * The QA equipment of one named group of a beam analysis work item, laid out
 * as in the task setup: the detector interface and its array as one connected
 * pair, the rest as plain tiles. Below the pair the user confirms that the
 * array of the set is the one physically connected (the interface reports
 * only its type); until then the zero measurement is blocked, since a zero
 * measurement taken with the wrong array would be attributed to this one.
 * A zero measurement is started from the
 * bridge between the pair and runs right here on the pair, not in a dialog:
 * the button morphs into the progress display at the click, before the run
 * reports anything, and pressing it then cancels the run after a
 * confirmation, returning the pair to idle at once. Once a run ended by itself the validity on the button's face is
 * reloaded. A run that finished shows its check on the grown button first;
 * the button drops back to idle once the check has settled and the validity
 * has arrived, so the face goes straight from the full progress to the full
 * validity. The button's face shows how much of the latest zero
 * measurement's validity is left. It can only be pressed while the interface
 * is online and reports a connected array; otherwise it is greyed and names
 * the reason. Leaving the page stops a run that is under way or still
 * starting; the measurement composable does that itself. Collapsing the
 * work item's panel does not: the panel keeps its content (see the work
 * list), so the run carries on out of sight.
 */
const props = defineProps<{
  systems: QaEquipmentCatalogDto[];
  /** The job occurrence the work item belongs to; the detector confirmation is kept per occurrence. */
  jobOccurrenceId?: string;
  /** Name of the linac of the job occurrence, named in the detector confirmation. */
  targetSystemName?: string;
}>();

const orderedSystems = computed(() => orderSystemsForBeamAnalysis(props.systems));

// Without a complete pair everything renders in the plain grid.
const showPairRow = computed(() => isBeamAnalysisPair(orderedSystems.value));

const pairRowSystems = computed(() => (showPairRow.value ? orderedSystems.value.slice(0, 2) : []));

const gridSystems = computed(() =>
  showPairRow.value ? orderedSystems.value.slice(2) : orderedSystems.value,
);

const { t } = useTranslation();

const { start, reset, cancel, status, starting, notifications } = useZeroMeasurement();

// Under way from the click until the run ended. Counting the pending start
// keeps the button in its cancel state from the first click on, so a second
// click cannot start a second run on the device.
const running = computed(
  () => starting.value || isActiveMeasurementStatus(status.value?.measurementStatus),
);

// Progress shown on the button while running: the backend's percent, 0 while
// the device is still being prepared (as the bar on the pair does). The
// button also gets the status itself, for its ring text and tooltip.
const progress = computed(() =>
  running.value ? (status.value?.measurementProgressPercent ?? 0) : undefined,
);

// Whether the user confirmed the set's array as the one connected, for this
// occurrence and this browser session. Asked below the pair; the zero
// measurement waits for it.
const {
  confirmed: detectorConfirmed,
  confirm: confirmDetector,
  reset: resetDetectorConfirmation,
} = useDetectorConfirmation(
  () => props.jobOccurrenceId,
  () => pairRowSystems.value[1]?.id,
);

// The interface's live presence, for the reported line of the confirmation.
const interfacePresence = useDeviceStatusSignalR(() => pairRowSystems.value[0]?.id);

// Why the start button is blocked: the detector is not confirmed yet, or the
// interface's live presence says so. While our own run is under way the
// interface is busy too, but the progress display takes precedence over the
// block. The busy reason stays once our run was dropped here but not on the
// device, e.g. after a failed stop.
const blocker = useZeroMeasurementBlocker(() => pairRowSystems.value[0]);
const blockedReasonKeys = {
  busy: 'ZeroMeasurementBlockedInterfaceBusy',
  offline: 'ZeroMeasurementBlockedInterfaceOffline',
  noArray: 'ZeroMeasurementBlockedNoArray',
} as const;
const blockedReason = computed(() => {
  if (running.value) {
    return undefined;
  }
  if (!detectorConfirmed.value) {
    return t('ZeroMeasurementBlockedDetectorUnconfirmed');
  }
  if (!blocker.value) {
    return undefined;
  }
  return t(blockedReasonKeys[blocker.value]);
});

// Validity of the pair's latest zero measurement, shown on the start button.
// Reloaded once a run ended, since the backend has stored it by then.
const { validity: zeroMeasurementValidity, load: reloadLatestZeroMeasurement } =
  useLatestZeroMeasurement(
    () => pairRowSystems.value[1]?.id,
    () => pairRowSystems.value[0]?.id,
  );

// The outcome phase after a finished run: the button shows its check, still
// grown. Cleared once the check has settled and the reload of the validity
// has answered, whichever comes last; a failed reload answers too.
const succeeded = ref(false);
let validityReload: Promise<unknown> = Promise.resolve();

async function startZeroMeasurement() {
  reset();
  succeeded.value = false;
  await start(orderedSystems.value.map((system) => system.id));
}

async function settleZeroMeasurement() {
  try {
    await validityReload;
  } finally {
    succeeded.value = false;
  }
}

// Sends the stop and drops the pair back to idle at once, without waiting
// for the answer or the device's status update: resetting clears the watched
// stream, so the button, the particles and the status line react to the
// click. The cancel still reaches the right measurement, since it carries the
// id from the start promise. The validity stays as it was: a cancelled run
// stores nothing.
function cancelZeroMeasurement() {
  cancel();
  reset();
}

watch(
  () => status.value?.measurementStatus,
  (measurementStatus) => {
    if (measurementStatus === 'Finished' || measurementStatus === 'Error') {
      validityReload = reloadLatestZeroMeasurement();
      succeeded.value = measurementStatus === 'Finished';
    }
  },
);
</script>

<template>
  <div>
    <ZeroMeasurementPairRow
      v-if="showPairRow"
      :systems="pairRowSystems"
      :status="status"
      :notifications="notifications"
      class="mb-4"
    >
      <template #bridge>
        <ConfirmedAction @confirmed="cancelZeroMeasurement()">
          <template #default="{ confirmedAction }">
            <ZeroMeasurementStartButton
              :validity="zeroMeasurementValidity"
              :progress="progress"
              :status="status?.measurementStatus"
              :succeeded="succeeded"
              :blockedReason="blockedReason"
              @click="startZeroMeasurement()"
              @cancel="confirmedAction()"
              @settled="settleZeroMeasurement()"
            />
          </template>
          <template #dialog.text>
            {{ t('CancelZeroMeasurementConfirmation') }}
          </template>
        </ConfirmedAction>
      </template>
    </ZeroMeasurementPairRow>

    <!-- The margin sits on the wrapper: the box animates its own height when it
         swaps with the confirmed line, and a margin of its own would not follow. -->
    <div v-if="showPairRow" class="mb-6">
      <BeamAnalysisDetectorConfirmation
        :detectorInterface="pairRowSystems[0]"
        :array="pairRowSystems[1]"
        :presence="interfacePresence"
        :targetSystemName="targetSystemName"
        :confirmed="detectorConfirmed"
        :changeBlocked="running"
        @confirm="confirmDetector()"
        @change="resetDetectorConfirmation()"
      />
    </div>

    <v-row v-if="gridSystems.length" density="compact">
      <v-col
        v-for="gridSystem in gridSystems"
        :key="gridSystem.id"
        cols="12"
        lg="6"
        xxl="4"
        class="mb-4"
      >
        <RTSystemTile class="w-100 h-100" :item="gridSystem" />
      </v-col>
    </v-row>
  </div>
</template>
